import { Fragment, useMemo } from 'react';
import type { FC } from 'react';
import PropTypes from 'prop-types';
import { ResponsiveImage } from '../../responsive-image';
import { AssetInfoPropTypes, AssetType } from '../utils';
import { BrandSpecialistVideoContainer, ImageContainer, YouTubeContainer } from './AssetContainers';
import type { AssetInfo } from '../utils';

interface AssetProps {
    asset: AssetInfo;
    shouldLazyLoad?: boolean;
    hasHighFetchPriority?: boolean;
    isThumbnail?: boolean;
}

export const Asset: FC<AssetProps> = ({
    asset,
    shouldLazyLoad,
    isThumbnail,
    hasHighFetchPriority,
}) => {
    const { assetUrl, id, alt, title, assetType, previewUrl } = asset;
    const Wrapper = useMemo(() => {
        switch (assetType) {
            case AssetType.IMAGE:
                return ImageContainer;
            case AssetType.BRAND_SPECIALIST_VIDEO:
                return BrandSpecialistVideoContainer;
            case AssetType.YOUTUBE:
                return YouTubeContainer;
            default:
                return Fragment;
        }
    }, [assetType]);

    return (
        <Wrapper isThumbnail={isThumbnail}>
            {assetType === AssetType.BRAND_SPECIALIST_VIDEO ? (
                <img
                    id={id}
                    src={previewUrl}
                    className={`h-full w-full ${!isThumbnail ? 'object-contain' : 'object-cover'}`}
                    alt={alt}
                    title={title}
                    loading={shouldLazyLoad ? 'lazy' : undefined}
                    fetchpriority={hasHighFetchPriority ? 'high' : undefined}
                />
            ) : (
                <ResponsiveImage
                    id={id}
                    src={assetType === AssetType.IMAGE ? assetUrl : previewUrl}
                    className="h-full w-full object-cover"
                    alt={alt}
                    title={title}
                    loading={shouldLazyLoad ? 'lazy' : undefined}
                    fetchpriority={hasHighFetchPriority ? 'high' : undefined}
                />
            )}
        </Wrapper>
    );
};

Asset.propTypes = {
    asset: AssetInfoPropTypes.isRequired,
    shouldLazyLoad: PropTypes.bool,
    isThumbnail: PropTypes.bool,
    hasHighFetchPriority: PropTypes.bool,
};

Asset.defaultProps = {
    shouldLazyLoad: false,
    isThumbnail: false,
    hasHighFetchPriority: false,
};
